// Imported from the modules directly rather than the `generic` barrel, which also
// exports the `MetricChart` component: pulling that into a pure spec builder would
// drag a React component tree and its stylesheets along with it.
import { DEFAULT_MAX_TICKS_LIMIT } from '@/components/charts/generic/constants';
import { dayAxis } from '@/components/charts/generic/dayAxis';
import type { MetricChartSpec } from '@/components/charts/generic/types';
import type { DomainBiomassDay } from '@/domain/cages/overview.types';

import { daypartMetricSeries } from '../daypartSeries';

import type { DaypartChartSpecParams } from './types';

export interface GrowthChartSpecParams extends DaypartChartSpecParams {
    /** Already translated axis title. */
    axisLabel: string;
    /** Weight unit the API returned values in, e.g. `'g'`. */
    weightUnitTag: string;
}

/** Growth ticks read as whole units; the value under the cursor keeps precision. */
const GROWTH_TICK_DECIMALS = 0;
const GROWTH_VALUE_DECIMALS = 2;
/** Integer tick spacing so the axis reads 0, 1, 2, 3 rather than fractional steps. */
const GROWTH_TICK_STEP = 1;
/**
 * Floor for the growth axis when the series is empty or very small. Without this,
 * Chart.js collapses to a 0–1 domain and `stepSize: 1` leaves only a single
 * interval — the user expects a readable ladder of whole grams.
 */
const GROWTH_MIN_AXIS_MAX = 3;

/**
 * Largest |weightGrowthAvg| among dayparts that start visible, or `0` when
 * nothing is plotted on those series.
 *
 * Axis options must follow `activeDaypartTags`, not every graphed daypart:
 * inactive series are `hiddenByDefault` and excluded from the Chart.js scale,
 * so a large hidden night series must not suppress the narrow-range floor for
 * the visible day series.
 */
function maxActiveGrowthAbs(days: DomainBiomassDay[], activeDaypartTags: string[]): number {
    const active = new Set(activeDaypartTags);
    let maxAbs = 0;
    for (const day of days) {
        if (!active.has(day.daypartTag)) continue;
        const value = day.data.weightGrowthAvg;
        if (value == null) continue;
        maxAbs = Math.max(maxAbs, Math.abs(value));
    }
    return maxAbs;
}

/**
 * Use a 1-unit step while the range still fits a readable number of ticks.
 * Wider ranges leave `stepSize` unset so Chart.js can pick larger nice steps
 * (10, 20, …) that stay whole with `tickDecimals: 0`.
 */
function resolveGrowthStepSize(maxAbs: number): number | undefined {
    return maxAbs <= DEFAULT_MAX_TICKS_LIMIT ? GROWTH_TICK_STEP : undefined;
}

/**
 * Keep a minimum axis height so empty/narrow growth still shows 0, 1, 2, 3.
 * Larger series leave this unset and scale to the data.
 */
function resolveGrowthSuggestedMax(maxAbs: number): number | undefined {
    return maxAbs < GROWTH_MIN_AXIS_MAX ? GROWTH_MIN_AXIS_MAX : undefined;
}

/**
 * Average daily weight growth, one series per daypart.
 *
 * The y axis starts at zero, matching the legacy chart's `startYAxisAtZero`
 * modifier: growth is read as a rate against a fixed baseline, so letting the
 * axis float would exaggerate small day-to-day differences.
 *
 * Ticks are whole grams (`0`, `1`, `2`, `3`, …). On empty or narrow *active*
 * domains the axis is floored to at least 3 g with a step of 1, so Chart.js
 * does not collapse to a single 0–1 interval of fractional ticks. Scaling uses
 * `activeDaypartTags` so a large hidden daypart cannot suppress that floor.
 */
export function buildGrowthChartSpec(params: GrowthChartSpecParams): MetricChartSpec<'line'> {
    const { days, graphedDaypartTags, activeDaypartTags, daypartLabel, locale, axisLabel } = params;
    const maxAbs = maxActiveGrowthAbs(days, activeDaypartTags);

    return {
        kind: 'line',
        days: dayAxis(days, (day) => day.day),
        series: daypartMetricSeries({
            days,
            metric: 'weightGrowthAvg',
            graphedDaypartTags,
            activeDaypartTags,
            label: daypartLabel,
            keyPrefix: 'weightGrowthAvg',
        }),
        left: {
            label: axisLabel,
            unit: params.weightUnitTag,
            beginAtZero: true,
            tickDecimals: GROWTH_TICK_DECIMALS,
            valueDecimals: GROWTH_VALUE_DECIMALS,
            stepSize: resolveGrowthStepSize(maxAbs),
            suggestedMax: resolveGrowthSuggestedMax(maxAbs),
        },
        locale,
    };
}

/**
 * The most recent `all`-daypart day with positive growth.
 *
 * Drives the "last measurement" tiles beside the growth chart. Days are scanned
 * from the end, and a day whose growth is null or non-positive is skipped —
 * reproducing the legacy `day.data?.weightGrowthAvg > 0` condition, which also
 * excluded zero and negative growth from counting as a measurement.
 */
export function findLastPositiveGrowthDay(days: DomainBiomassDay[]): DomainBiomassDay | undefined {
    return days.findLast(
        (day) =>
            day.daypartTag === 'all' &&
            day.data.weightGrowthAvg !== null &&
            day.data.weightGrowthAvg > 0
    );
}
